// The one way a page points a workspace at TrakLog: for an upgrade to Pro,
// for a flag only TrakLog switches on, for more storage, or with a question.
// Every plan prompt renders this, so the wording cannot drift between pages.
//
//   <UpgradeContactLink workspaceName={name} />          inline: the word "TrakLog"
//   <UpgradeContactLink button workspaceName={name} />   a call-to-action
//   reason="enable" featureName="API Access"            ask for a switch-on
//
// It opens a short form and the server forwards the message. It used to be a
// `mailto:` link that printed the address, so every customer who met a plan
// limit saw it (product owner, 2026-10-07: the address is not published).
// Who is asking and for which workspace comes from the session on the server;
// `workspaceName` is only for the dialog's own wording.
import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Snackbar, TextField, Typography,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../apiConfig';
import { useAuth } from '../contexts/AuthContext';

export const contactTitle = (reason, featureName) => {
  if (reason === 'enable') return `Ask TrakLog to switch on ${featureName || 'a feature'}`;
  if (reason === 'storage') return 'Ask for more storage';
  if (reason === 'renew') return 'Renew the subscription';
  if (reason === 'upgrade') return 'Upgrade to Pro';
  return 'Contact TrakLog';
};

const UpgradeContactLink = ({
  workspaceName,
  reason = 'upgrade',
  featureName,
  button = false,
  children,
  ...rest
}) => {
  const { currentUser } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Actions never reload the page, and never sit on a spinner: the dialog
  // closes at once and says "Sent". If the server refuses, it opens again
  // with what was typed and says nothing was sent.
  const send = async () => {
    const typed = message;
    setOpen(false);
    setError('');
    setNotice('Sent to TrakLog. We reply by email.');
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      const response = await fetch(`${API_BASE_URL}/api/v1/contact/workspace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ reason, feature_name: featureName || null, message: typed }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Your message could not be sent.');
      }
      setMessage('');
    } catch (err) {
      setNotice('');
      setError(`${err.message || 'Your message could not be sent.'} Nothing was sent. Try again.`);
      setMessage(typed);
      setOpen(true);
    }
  };

  const title = contactTitle(reason, featureName);
  const openDialog = () => { setError(''); setOpen(true); };

  return (
    <>
      {button ? (
        <Button
          variant="contained" color="primary" startIcon={<EmailIcon />}
          data-contact-reason={reason} {...rest} onClick={openDialog}
        >
          {children || (reason === 'enable'
            ? 'Ask TrakLog to switch it on'
            : reason === 'storage' ? 'Ask for more storage' : reason === 'other' ? 'Contact TrakLog' : 'Upgrade to Pro')}
        </Button>
      ) : (
        <Link
          component="button" type="button" underline="always"
          sx={{ verticalAlign: 'baseline', font: 'inherit' }}
          data-contact-reason={reason} {...rest} onClick={openDialog}
        >
          {children || 'TrakLog'}
        </Link>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your message goes to TrakLog with your name, your email address and
            {workspaceName ? ` the workspace ${workspaceName}` : ' your workspace'}.
            We reply by email.
          </Typography>
          <TextField
            label="Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline minRows={3} fullWidth
            inputProps={{ maxLength: 4000 }}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={send}>Send</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={6000}
        onClose={() => setNotice('')}
        message={notice}
      />
    </>
  );
};

export default UpgradeContactLink;
